// The browser side of the pushes (taps as they happen, the Sunday summary): can this browser take
// them, which it gets, and changing that. The iOS catch: Safari only offers push to a site added
// to the Home Screen and opened from there, and permission must be asked from a tap.

import { api } from "../api/client";
import type { PushPrefs } from "../shared/types";
import { isProductionHost } from "../shared/origin";
import { isIos, isStandalone } from "./platform";

/** Why this browser cannot take pushes, or null if it can. */
export type PushBlock = "unsupported" | "install-ios" | "denied" | "not-production";

export function pushBlock(): PushBlock | null {
  // iOS before it is installed has no PushManager at all, so this is checked first to say why.
  if (isIos() && !isStandalone()) return "install-ios";
  if (!("serviceWorker" in navigator && "PushManager" in window && "Notification" in window)) return "unsupported";
  // Previews and localhost never register the service worker (register.ts), so there is
  // nothing to subscribe with, whatever the permission says.
  if (!isProductionHost(window.location.hostname)) return "not-production";
  if (Notification.permission === "denied") return "denied";
  return null;
}

/** The service worker, or a rejection rather than a wait that never ends. */
function serviceWorkerReady(timeoutMs = 8000): Promise<ServiceWorkerRegistration> {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("The app isn't ready yet. Reload it and try again.")), timeoutMs),
  );
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

function urlBase64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const b64 = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const NONE: PushPrefs = { taps: false, weekly: false };

/** Which pushes this browser gets right now: none unless both it and the server hold a subscription. */
export async function pushPrefs(): Promise<PushPrefs> {
  const reg = await serviceWorkerReady();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return NONE;
  return (await api.pushStatus(sub.endpoint)).prefs ?? NONE;
}

/**
 * Makes this browser get exactly `prefs`: subscribes it if it is not yet (call that straight
 * from a tap, or iOS refuses to ask permission), tells the server the new choices if it is, and
 * unsubscribes it when both are off.
 */
export async function setPushPrefs(prefs: PushPrefs): Promise<void> {
  const on = prefs.taps || prefs.weekly;
  // Before any other await: iOS only asks from a tap, and waiting on something first loses it.
  if (on && Notification.permission !== "granted" && (await Notification.requestPermission()) !== "granted") {
    throw new Error("Notifications weren't allowed.");
  }
  const reg = await serviceWorkerReady();
  let sub = await reg.pushManager.getSubscription();
  if (!on) {
    if (!sub) return;
    // The server first, so a failure there leaves the browser subscribed and the switch on.
    await api.unsubscribePush(sub.endpoint);
    await sub.unsubscribe();
    return;
  }
  if (!sub) {
    const { key } = await api.vapidKey();
    if (!key) throw new Error("Notifications aren't set up on this server yet.");
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(key) });
  }
  await api.subscribePush(sub.toJSON(), prefs);
}

/**
 * This browser's push endpoint, sent with each tap so the push about it skips this phone. Never
 * waits for a service worker that is not there (previews, localhost): it is null then.
 */
export async function ownEndpoint(): Promise<string | null> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    return (await reg?.pushManager?.getSubscription())?.endpoint ?? null;
  } catch {
    return null;
  }
}

/** Sends this browser a test push, and says in words what happened. */
export async function sendTestPush(): Promise<string> {
  const reg = await serviceWorkerReady();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) throw new Error("Turn on a notification above first.");
  const { outcome } = await api.testPush(sub.endpoint);
  if (outcome === "sent") return "Sent. It should arrive in a few seconds.";
  if (outcome === "logged") return "Notifications aren't set up on this server, so nothing was sent.";
  if (outcome === "gone")
    throw new Error("This phone's notifications had expired. Turn the switches off and on again.");
  throw new Error("Couldn't reach the notification service. Try again in a minute.");
}

/** Stops every push to this browser (signing out). */
export function unsubscribe(): Promise<void> {
  return setPushPrefs(NONE);
}
